import React, { useEffect, useState } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Drawer, Empty, Spin, Badge } from "antd";
import { useSelector } from "react-redux";
import {
  useGetAdminNotificationsQuery,
  useGetOrganizationNotificationsQuery,
  useGetInterpreterNotificationsQuery,
  useDeleteNotificationsMutation,
} from "../../store/api/apiSlice";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const NotificationsPanel = ({ visible, onClose }) => {
  const { user, role } = useSelector((state) => state.auth);
  const [notifications, setNotifications] = useState({ data: [] });

  // Use polling to refresh notifications frequently
  const {
    data: adminData,
    isLoading: adminLoading,
    refetch: refetchAdminNotifications,
  } = useGetAdminNotificationsQuery(undefined, {
    skip: role !== "admin",
    pollingInterval: 10000, // Poll every 10 seconds
    refetchOnMountOrArgChange: true,
  });

  const {
    data: orgData,
    isLoading: orgLoading,
    refetch: refetchOrgNotifications,
  } = useGetOrganizationNotificationsQuery(user?.organization?._id, {
    skip: !user?.organization?._id || role !== "organization",
    pollingInterval: 10000, // Poll every 10 seconds
    refetchOnMountOrArgChange: true,
  });

  const {
    data: interpreterData,
    isLoading: interpreterLoading,
    refetch: refetchInterpreterNotifications,
  } = useGetInterpreterNotificationsQuery(user?.interpreter?._id, {
    skip: !user?.interpreter?._id || role !== "interpreter",
    pollingInterval: 10000, // Poll every 10 seconds
    refetchOnMountOrArgChange: true,
  });

  const [deleteNotifications] = useDeleteNotificationsMutation();

  // Update notifications whenever data changes
  useEffect(() => {
    let notificationData;

    switch (role) {
      case "admin":
        notificationData = adminData;
        break;
      case "organization":
        notificationData = orgData;
        break;
      case "interpreter":
        notificationData = interpreterData;
        break;
      default:
        notificationData = null;
    }

    if (notificationData) {
      setNotifications(notificationData);
    }
  }, [adminData, orgData, interpreterData, role]);

  // Force refetch when drawer opens
  useEffect(() => {
    if (visible) {
      // Force refetch based on role
      if (role === "admin") {
        refetchAdminNotifications();
      } else if (role === "organization" && user?.organization?._id) {
        refetchOrgNotifications();
      } else if (role === "interpreter" && user?.interpreter?._id) {
        refetchInterpreterNotifications();
      }
    }
  }, [
    visible,
    role,
    user,
    refetchAdminNotifications,
    refetchOrgNotifications,
    refetchInterpreterNotifications,
  ]);

  const handleDeleteNotification = async (notificationId) => {
    try {
      // Call the mutation with the proper format - pass an array with the notification ID
      await deleteNotifications([notificationId]);

      // Update local state to immediately remove the notification
      setNotifications((prev) => ({
        ...prev,
        data: prev.data.filter((item) => item._id !== notificationId),
      }));
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const groupNotificationsByDate = (notifications) => {
    if (!notifications?.data || !Array.isArray(notifications.data)) {
      return {};
    }

    return notifications.data.reduce((groups, notification) => {
      const date = dayjs(notification.date_created).format("YYYY-MM-DD");
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(notification);
      return groups;
    }, {});
  };

  const isLoading = adminLoading || orgLoading || interpreterLoading;
  const groupedNotifications = groupNotificationsByDate(notifications);
  const hasNotifications = notifications?.data && notifications.data.length > 0;

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      placement="right"
      width={400}
      closable={false}
      rootStyle={{ position: "absolute" }}
      styles={{ body: { padding: 0 } }}
      className="notifications-drawer"
    >
      <div className="h-full flex flex-col bg-white">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-md font-semibold m-0">
            Notifications
            {hasNotifications && (
              <Badge
                count={notifications.data.length}
                style={{ marginLeft: 8 }}
              />
            )}
          </h3>
          <CloseOutlined
            onClick={onClose}
            className="text-red-600 text-lg cursor-pointer"
          />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Spin size="large" />
          </div>
        ) : !hasNotifications ? (
          <div className="flex items-center justify-center h-full">
            <Empty description="No notifications" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {Object.entries(groupedNotifications).map(([date, items]) => (
              <div key={date} className="p-4">
                <h4 className="text-sm font-medium text-gray-500 mb-2">
                  {dayjs(date).isSame(dayjs(), "day")
                    ? "Today"
                    : dayjs(date).isSame(dayjs().subtract(1, "day"), "day")
                    ? "Yesterday"
                    : dayjs(date).format("MMMM D, YYYY")}
                </h4>
                <div className="rounded-2xl border p-1">
                  {items.map((notification, index) => (
                    <div
                      key={notification._id}
                      className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-gray-50 ${
                        index !== items.length - 1
                          ? "border-b border-gray-100"
                          : ""
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                        {notification.type === "admin" ? "👨‍💼" : "📌"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 my-0">
                          {notification.type.charAt(0).toUpperCase() +
                            notification.type.slice(1)}{" "}
                          Notification
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {dayjs(notification.date_created).fromNow()}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNotification(notification._id);
                        }}
                        className="bg-white border-0"
                      >
                        <CloseOutlined />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default NotificationsPanel;
